export type LocalModelPhase = 'disabled' | 'initializing' | 'syncing' | 'ready' | 'degraded' | 'failed'
export type LocalModelRoute = 'local' | 'remote' | 'mixed' | null

export interface LocalModelError {
  name: string
  message: string
  code: string
  stage: 'initialization' | 'boot-seed' | 'synchronization' | 'query'
  retryable: boolean
}

export interface LocalModelLatency {
  samples: number
  p50Ms: number | null
  p90Ms: number | null
  p95Ms: number | null
  p99Ms: number | null
}

/** Session counters for whether the local read model avoided server work. */
export interface LocalModelQueryStats {
  local: number
  remote: number
  mixed: number
  /** Reads answered entirely from the local model, so no server round-trip was made. */
  roundtripsSaved: number
  /** Percentiles over the most recent local reads. */
  localLatency: LocalModelLatency
  /** Percentiles over the most recent reads that still called the server. */
  remoteLatency: LocalModelLatency
  /** Newest last. Remote or mixed reads, so a miss is visible without the console. */
  recentRemote: LocalModelRemoteRead[]
}

export interface LocalModelRemoteRead {
  operation: 'find' | 'get'
  reason: string
  ms: number
  label: string
  at: number
}

export interface LocalModelRouteSample {
  ms: number
  operation: 'find' | 'get'
  label?: string
}

/** Where a download is, and how long the tab should stay open. */
/** Download stages, in order. Package documents are in the trees; their files are the last two stages. */
export const LOCAL_MODEL_STAGES = ['site', 'system', 'apps', 'packages', 'binary content'] as const

export interface LocalModelActivity {
  phase: LocalModelPhase
  /** site, system, apps, packages, or binary content. */
  stage: string | null
  /** The full stage list, so a UI can show what is still ahead. */
  stages: readonly string[]
  /** Index of `stage` in `stages`, or -1 when nothing is downloading. */
  stageIndex: number
  /** Sync step, such as `receiving-scope`. Null when no download is in progress. */
  step: string | null
  profileId: string | null
  scopeId: string | null
  message: string | null
  receivedDocuments: number | null
  completedScopes: number | null
  scopeCount: number | null
  /** Milliseconds until the known remainder should finish. Null until a rate exists. */
  remainingMs: number | null
}

/**
 * This tab's local-model worker. `owner` holds the database and runs every
 * query and sync; `follower` forwards to another tab's owner; `retired`: a
 * newer build took over and this tab must reload. `none` until a worker
 * reports.
 */
export type LocalModelRole = 'none' | 'starting' | 'owner' | 'follower' | 'retired'

export type LocalModelJournalKind = 'boot' | 'role' | 'migration' | 'sync' | 'stall' | 'fallback' | 'error'

/** One lifecycle entry, kept across boots by the owning worker. */
export interface LocalModelJournalEntry {
  seq: number
  /** ISO time. */
  at: string
  kind: LocalModelJournalKind
  /** For example `ready`, `owner`, `import-started`, `format`, `profile`, `busy`, `lookup`. */
  event: string
  durationMs: number | null
  /** The tab that reported the entry, when a page did. */
  tabId: string | null
  workerId: string | null
  version: LocalModelVersion | null
  detail: Record<string, any> | null
}

export interface LocalModelHistoryOptions {
  /** ISO time or epoch milliseconds. */
  since?: string | number
  kinds?: LocalModelJournalKind[]
  /** Newest entries kept; default 200. */
  limit?: number
}

/** A running import, new database generation or format migration. */
export interface LocalModelMigration {
  kind: string
  reason: string | null
  /** What the crossed schema revisions improved. */
  notes: Array<{ revision: number, note: string }>
  startedAt: string
  progress: { profileId?: string, phase?: string, documentCount?: number } | null
}

export interface LocalModelBoot {
  startedAt: string
  readyAt?: string
  readyAfterMs: number | null
  decision: { outcome: string, reason?: string } | null
  outcome?: string | null
  migrations: LocalModelJournalEntry[]
}

/** @internal How LocalModel switches the local model off and on for this browser. */
export interface LocalModelSwitchHandlers {
  disable(): Promise<void>
  enable(): Promise<void>
  isDisabled(): boolean
}

/** @internal How LocalModel reads the worker's journal. */
export interface LocalModelJournalSource {
  read(options?: LocalModelHistoryOptions): Promise<LocalModelJournalEntry[]>
  subscribe(handler: (entry: LocalModelJournalEntry) => void): () => void
}

export interface LocalModelVersion {
  package: string
  schema: number
  build: number
}

export interface LocalModelState {
  configured: boolean
  enabled: boolean
  available: boolean
  ready: boolean
  phase: LocalModelPhase
  storage: string | null
  progress: Record<string, any> | null
  /** Phase plus the current download, including an estimate of time remaining. */
  activity: LocalModelActivity
  stats: Record<string, any> | null
  error: LocalModelError | null
  retryable: boolean
  lastRoute: LocalModelRoute
  lastReason: string | null
  localReads: number
  remoteFallbacks: number
  queries: LocalModelQueryStats
  role: LocalModelRole
  migration: LocalModelMigration | null
  updatedAt: number
}

export interface LocalModelApi {
  readonly state: Readonly<LocalModelState>
  readonly configured: boolean
  readonly enabled: boolean
  readonly available: boolean
  readonly ready: boolean
  readonly retryable: boolean
  /** The local read model's package version, schema revision and build, as its worker reports them. */
  readonly version: LocalModelVersion | null
  readonly role: LocalModelRole
  /** The running migration, or `null`; also announced as a `migration` entry. */
  readonly migration: LocalModelMigration | null
  /** The last page boot this browser recorded, from the worker's journal. */
  readonly lastBoot: LocalModelBoot | null
  retry(): Promise<Readonly<LocalModelState>>
  subscribe(listener: (state: Readonly<LocalModelState>) => void): () => void
  /** Whether this browser has the local model switched off with `disable()`. */
  readonly disabled: boolean
  /**
   * Switch the local model off in this browser: this tab sends every read to
   * the server from now on, and later page loads do not start it, even when
   * the feature is enabled. The local data is kept.
   */
  disable(): Promise<void>
  /** Switch it on again; starts it in this tab when the feature is enabled. */
  enable(): Promise<void>
  /** The database owner's reader workers (configuration, readiness, counters), from the latest stats. */
  readonly readers: Record<string, any> | null
  /**
   * Turn reader workers on or off, or set their count, at runtime:
   * `true`, `false` or `{ enabled, count }`. Resolves with their status.
   */
  setReaders(config: boolean | { enabled?: boolean, count?: number }): Promise<Record<string, any>>
  /** Stored lifecycle entries across boots, oldest first. Empty without a local model. */
  history(options?: LocalModelHistoryOptions): Promise<LocalModelJournalEntry[]>
  /** Live entries of one kind, or `*` for all; returns an unsubscribe function. */
  on(kind: LocalModelJournalKind | '*', handler: (entry: LocalModelJournalEntry) => void): () => void
}

export interface LocalModelErrorContext {
  code: string
  stage: LocalModelError['stage']
  retryable?: boolean
}

/** @internal */
export interface LocalModelMonitor {
  markConfigured(): void
  featureDisabled(): void
  initializing(): void
  adapterAvailable(): void
  syncing(): void
  reportStatus(status: Record<string, any>): void
  reportProgress(progress: Record<string, any>): void
  reportStats(stats: Record<string, any>): void
  markReady(stats?: Record<string, any>): void
  markFailed(error: any, context?: LocalModelErrorContext): void
  markDegraded(error: any, context?: LocalModelErrorContext): void
  reportRole(role: LocalModelRole): void
  reportMigration(migration: LocalModelMigration | null): void
  attachJournal(source: LocalModelJournalSource | null): void
  setReadersHandler(handler: ((config: any) => Promise<any>) | null): void
  setSwitchHandlers(handlers: LocalModelSwitchHandlers | null): void
  recordRoute(route: Exclude<LocalModelRoute, null>, reason?: string, sample?: LocalModelRouteSample): void
}

const INITIAL_ACTIVITY: LocalModelActivity = {
  phase: 'disabled',
  stage: null,
  stages: LOCAL_MODEL_STAGES,
  stageIndex: -1,
  step: null,
  profileId: null,
  scopeId: null,
  message: null,
  receivedDocuments: null,
  completedScopes: null,
  scopeCount: null,
  remainingMs: null,
}

const INITIAL_STATE: LocalModelState = {
  configured: false,
  enabled: false,
  available: false,
  ready: false,
  phase: 'disabled',
  storage: null,
  progress: null,
  activity: INITIAL_ACTIVITY,
  stats: null,
  error: null,
  retryable: false,
  lastRoute: null,
  lastReason: null,
  localReads: 0,
  remoteFallbacks: 0,
  queries: emptyQueryStats(),
  role: 'none',
  migration: null,
  updatedAt: Date.now(),
}

const LATENCY_SAMPLES = 256
const RECENT_REMOTE_LIMIT = 10

function emptyLatency(): LocalModelLatency {
  return { samples: 0, p50Ms: null, p90Ms: null, p95Ms: null, p99Ms: null }
}

function emptyQueryStats(): LocalModelQueryStats {
  return {
    local: 0,
    remote: 0,
    mixed: 0,
    roundtripsSaved: 0,
    localLatency: emptyLatency(),
    remoteLatency: emptyLatency(),
    recentRemote: [],
  }
}

function percentile(sorted: number[], fraction: number): number | null {
  if (!sorted.length) return null
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(fraction * sorted.length) - 1))
  return Math.round(sorted[index] * 10) / 10
}

function latencyFrom(samples: number[]): LocalModelLatency {
  if (!samples.length) return emptyLatency()
  const sorted = samples.slice().sort((left, right) => left - right)
  return {
    samples: sorted.length,
    p50Ms: percentile(sorted, 0.50),
    p90Ms: percentile(sorted, 0.90),
    p95Ms: percentile(sorted, 0.95),
    p99Ms: percentile(sorted, 0.99),
  }
}

function normalizeError(error: any, context: LocalModelErrorContext): LocalModelError {
  return {
    name: error?.name || 'Error',
    message: error?.message || String(error),
    code: error?.code || context.code,
    stage: context.stage,
    retryable: context.retryable ?? true,
  }
}

type LocalModelEvent =
  | { type: 'configured' }
  | { type: 'feature-disabled' }
  | { type: 'initialization-started' }
  | { type: 'adapter-available' }
  | { type: 'synchronization-started' }
  | { type: 'status-reported', status: Record<string, any> }
  | { type: 'progress-reported', progress: Record<string, any>|null }
  | { type: 'stats-reported', stats: Record<string, any>|null }
  | { type: 'ready', stats?: Record<string, any> }
  | { type: 'failed', error: LocalModelError }
  | { type: 'degraded', error: LocalModelError }
  | { type: 'role-reported', role: LocalModelRole }
  | { type: 'migration-reported', migration: LocalModelMigration | null }
  | { type: 'route-recorded', route: Exclude<LocalModelRoute, null>, reason?: string, sample?: LocalModelRouteSample, localLatency: LocalModelLatency, remoteLatency: LocalModelLatency }

function transitionLocalModel(state: LocalModelState, event: LocalModelEvent): LocalModelState {
  let patch: Partial<LocalModelState>
  switch (event.type) {
    case 'configured':
      patch = { configured: true }
      break
    case 'feature-disabled':
      patch = {
        enabled: false,
        available: false,
        ready: false,
        phase: 'disabled',
        progress: null,
        error: null,
        retryable: false,
      }
      break
    case 'initialization-started':
      patch = {
        configured: true,
        enabled: true,
        available: false,
        ready: false,
        phase: 'initializing',
        error: null,
        retryable: false,
      }
      break
    case 'adapter-available':
      patch = { configured: true, enabled: true, available: true, error: null, retryable: false }
      break
    case 'synchronization-started':
      patch = { available: true, ready: state.ready, phase: 'syncing', error: null, retryable: false }
      break
    case 'status-reported':
      patch = event.status?.phase === 'initializing'
        ? { phase: 'initializing' }
        : event.status?.phase === 'ready' && state.phase !== 'syncing'
          ? { phase: 'ready', ready: true }
          : {}
      break
    case 'progress-reported': {
      const terminal = event.progress?.phase === 'complete'
        || event.progress?.phase === 'failed'
        || event.progress?.phase === 'offline'
      const stage = stageName(event.progress)
      // Package and binary downloads run after the model is ready; they show
      // as activity, not as a sync of the model.
      const background = stage === 'packages' || stage === 'binary content'
      if (terminal) {
        patch = state.ready && state.phase === 'syncing'
          ? { progress: event.progress, phase: 'ready' }
          : { progress: event.progress }
      } else if (background && state.ready) {
        patch = { progress: event.progress }
      } else {
        patch = { progress: event.progress, phase: 'syncing', ready: state.ready }
      }
      break
    }
    case 'stats-reported':
      patch = {
        stats: event.stats,
        storage: typeof event.stats?.storage === 'string' ? event.stats.storage : state.storage,
      }
      break
    case 'ready':
      patch = {
        available: true,
        ready: true,
        phase: 'ready',
        error: null,
        retryable: false,
        ...(event.stats
          ? {
              stats: { ...event.stats },
              storage: typeof event.stats.storage === 'string' ? event.stats.storage : state.storage,
            }
          : {}),
      }
      break
    case 'failed':
      patch = { available: false, ready: false, phase: 'failed', error: event.error, retryable: event.error.retryable }
      break
    case 'degraded':
      patch = { available: true, ready: state.ready, phase: 'degraded', error: event.error, retryable: event.error.retryable }
      break
    case 'role-reported':
      patch = { role: event.role }
      break
    case 'migration-reported':
      patch = { migration: event.migration }
      break
    case 'route-recorded': {
      const hitServer = event.route === 'remote' || event.route === 'mixed'
      const recentRemote = hitServer
        ? state.queries.recentRemote.concat({
          operation: event.sample?.operation ?? 'find',
          reason: event.reason || event.route,
          ms: event.sample?.ms ?? 0,
          label: event.sample?.label || '',
          at: Date.now(),
        }).slice(-RECENT_REMOTE_LIMIT)
        : state.queries.recentRemote
      patch = {
        lastRoute: event.route,
        lastReason: event.reason || null,
        localReads: state.localReads + (event.route === 'local' || event.route === 'mixed' ? 1 : 0),
        remoteFallbacks: state.remoteFallbacks + (hitServer ? 1 : 0),
        queries: {
          local: state.queries.local + (event.route === 'local' ? 1 : 0),
          remote: state.queries.remote + (event.route === 'remote' ? 1 : 0),
          mixed: state.queries.mixed + (event.route === 'mixed' ? 1 : 0),
          roundtripsSaved: state.queries.roundtripsSaved + (event.route === 'local' ? 1 : 0),
          localLatency: event.localLatency,
          remoteLatency: event.remoteLatency,
          recentRemote,
        },
      }
      break
    }
  }
  return withActivity({ ...state, ...patch, updatedAt: Date.now() })
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || value.trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function stageName(progress: Record<string, any> | null): string | null {
  if (!progress) return null
  if (progress.phase === 'complete' || progress.phase === 'failed' || progress.phase === 'offline') return null
  if (progress.profileId === 'binary content' || progress.phase === 'receiving-binaries') return 'binary content'
  if (progress.profileId === 'packages' || progress.phase === 'receiving-packages') return 'packages'
  const profileId = progress.profileId
  return profileId === 'site' || profileId === 'system' || profileId === 'apps' ? profileId : null
}

function documentsStillExpected(progress: Record<string, any>, stats: Record<string, any> | null, received: number): number | null {
  const expected = finiteNumber(progress.expectedDocuments)
  if (expected != null) return received > expected ? null : expected - received
  const scopes = Array.isArray(stats?.scopes) ? stats.scopes : []
  const current = scopes.find(scope => scope?.profileId === progress.profileId && scope?.scopeId === progress.scopeId)
  const known = finiteNumber(current?.documentCount)
  if (known == null || received > known) return null
  let remaining = known - received
  const scopeIndex = finiteNumber(progress.scopeIndex)
  const scopeCount = finiteNumber(progress.scopeCount)
  if (scopeIndex == null || scopeCount == null || scopeCount - scopeIndex <= 1) return remaining
  for (const scope of scopes) {
    if (!scope || scope.profileId !== progress.profileId || scope.scopeId === progress.scopeId) continue
    const count = finiteNumber(scope.documentCount)
    if (count == null) return null
    remaining += count
  }
  return remaining
}

function deriveActivity(state: LocalModelState): LocalModelActivity {
  const progress = state.progress
  let stage = stageName(progress)
  if (state.phase === 'ready' && stage !== 'packages' && stage !== 'binary content') stage = null
  return {
    phase: state.phase,
    stage,
    stages: LOCAL_MODEL_STAGES,
    stageIndex: stage == null ? -1 : LOCAL_MODEL_STAGES.indexOf(stage as typeof LOCAL_MODEL_STAGES[number]),
    step: stage && typeof progress?.phase === 'string' ? progress.phase : null,
    profileId: typeof progress?.profileId === 'string' ? progress.profileId : null,
    scopeId: typeof progress?.scopeId === 'string' ? progress.scopeId : null,
    message: typeof progress?.message === 'string' ? progress.message : null,
    receivedDocuments: finiteNumber(progress?.receivedDocuments),
    completedScopes: finiteNumber(progress?.completedScopes),
    scopeCount: finiteNumber(progress?.scopeCount),
    remainingMs: stage ? finiteNumber(progress?.remainingMs) : state.phase === 'ready' ? 0 : null,
  }
}

function withActivity(state: LocalModelState): LocalModelState {
  return { ...state, activity: deriveActivity(state) }
}

/**
 * Observable lifecycle and routing information for the optional browser-local
 * read model. The public surface is intentionally read-only; Aeppic's
 * local-first adapter owns lifecycle updates.
 */
export class LocalModel implements LocalModelApi, LocalModelMonitor {
  private _state: LocalModelState = { ...INITIAL_STATE, queries: emptyQueryStats() }
  private _listeners = new Set<(state: Readonly<LocalModelState>) => void>()
  private _retryHandler: (() => Promise<any>) | null = null
  private _localDurations: number[] = []
  private _remoteDurations: number[] = []
  private _downloadBaseline: { key: string, at: number, received: number } | null = null
  private _readersHandler: ((config: any) => Promise<any>) | null = null
  private _switchHandlers: LocalModelSwitchHandlers | null = null
  private _journal: LocalModelJournalSource | null = null
  private _stopJournal: (() => void) | null = null
  private _entryListeners = new Map<string, Set<(entry: LocalModelJournalEntry) => void>>()
  private _lastBoot: LocalModelBoot | null = null
  private _bootMigrations: LocalModelJournalEntry[] = []

  get state(): Readonly<LocalModelState> {
    return Object.freeze({ ...this._state })
  }

  get configured() { return this._state.configured }
  get enabled() { return this._state.enabled }
  get available() { return this._state.available }
  get ready() { return this._state.ready }
  get retryable() { return this._state.retryable }
  get version(): LocalModelVersion | null { return this._state.stats?.version ?? null }
  get role(): LocalModelRole { return this._state.role }
  get migration(): LocalModelMigration | null { return this._state.migration }
  get readers(): Record<string, any> | null { return this._state.stats?.readers ?? null }

  get disabled(): boolean { return this._switchHandlers?.isDisabled() ?? false }

  async disable() {
    if (!this._switchHandlers) throw new Error('Switching the local read model is not available')
    await this._switchHandlers.disable()
  }

  async enable() {
    if (!this._switchHandlers) throw new Error('Switching the local read model is not available')
    await this._switchHandlers.enable()
  }

  /** @internal */
  setSwitchHandlers(handlers: LocalModelSwitchHandlers | null) {
    this._switchHandlers = handlers
  }

  async setReaders(config: boolean | { enabled?: boolean, count?: number }) {
    if (!this._readersHandler) throw new Error('Local read model readers are not available')
    return this._readersHandler(config)
  }

  /** @internal */
  setReadersHandler(handler: ((config: any) => Promise<any>) | null) {
    this._readersHandler = handler
  }

  get lastBoot(): LocalModelBoot | null { return this._lastBoot ?? this._state.stats?.lastBoot ?? null }

  async history(options: LocalModelHistoryOptions = {}): Promise<LocalModelJournalEntry[]> {
    if (!this._journal) return []
    return this._journal.read(options)
  }

  on(kind: LocalModelJournalKind | '*', handler: (entry: LocalModelJournalEntry) => void) {
    let listeners = this._entryListeners.get(kind)
    if (!listeners) {
      listeners = new Set()
      this._entryListeners.set(kind, listeners)
    }
    listeners.add(handler)
    return () => { listeners.delete(handler) }
  }

  /** @internal */
  attachJournal(source: LocalModelJournalSource | null) {
    this._stopJournal?.()
    this._journal = source
    this._stopJournal = source ? source.subscribe(entry => this._deliver(entry)) : null
  }

  /** This page's boot, from the entries seen while it started. */
  private _noteBoot(entry: LocalModelJournalEntry) {
    if (entry?.kind === 'migration' && !entry.event?.endsWith('-started')) this._bootMigrations.push(entry)
    if (entry?.kind !== 'boot' || entry.event !== 'ready' || this._lastBoot) return
    this._lastBoot = {
      startedAt: entry.detail?.startedAt ?? entry.at,
      readyAt: entry.at,
      readyAfterMs: entry.detail?.readyAfterMs ?? entry.durationMs,
      decision: entry.detail?.decision ?? null,
      outcome: entry.detail?.outcome ?? null,
      migrations: this._bootMigrations,
    }
  }

  /** @internal */
  reportRole(role: LocalModelRole) {
    if (role !== this._state.role) this._dispatch({ type: 'role-reported', role })
  }

  /** @internal */
  reportMigration(migration: LocalModelMigration | null) {
    this._dispatch({ type: 'migration-reported', migration: migration ?? null })
  }

  private _deliver(entry: LocalModelJournalEntry) {
    this._noteBoot(entry)
    for (const kind of [entry?.kind, '*']) {
      for (const handler of this._entryListeners.get(kind) ?? []) {
        try { handler(entry) } catch { /* A UI listener cannot break the model. */ }
      }
    }
  }

  async retry(): Promise<Readonly<LocalModelState>> {
    if (!this._retryHandler) throw new Error('Local read model retry is not available')
    await this._retryHandler()
    return this.state
  }

  /** @internal */
  setRetryHandler(handler: (() => Promise<any>) | null) {
    this._retryHandler = handler
  }

  subscribe(listener: (state: Readonly<LocalModelState>) => void) {
    this._listeners.add(listener)
    try { listener(this.state) } catch { /* A UI subscriber cannot break routing. */ }
    return () => this._listeners.delete(listener)
  }

  markConfigured() {
    this._dispatch({ type: 'configured' })
  }

  featureDisabled() {
    this._dispatch({ type: 'feature-disabled' })
  }

  initializing() {
    this._dispatch({ type: 'initialization-started' })
  }

  adapterAvailable() {
    this._dispatch({ type: 'adapter-available' })
  }

  syncing() {
    this._dispatch({ type: 'synchronization-started' })
  }

  reportStatus(status: Record<string, any>) {
    this._dispatch({ type: 'status-reported', status })
  }

  reportProgress(progress: Record<string, any>) {
    if (!progress) {
      this._downloadBaseline = null
      this._dispatch({ type: 'progress-reported', progress: null })
      return
    }
    const remainingMs = this._estimateRemainingMs(progress)
    this._dispatch({
      type: 'progress-reported',
      progress: { ...progress, remainingMs },
    })
  }

  reportStats(stats: Record<string, any>) {
    this._dispatch({ type: 'stats-reported', stats: stats ? { ...stats } : null })
  }

  markReady(stats?: Record<string, any>) {
    this._downloadBaseline = null
    this._dispatch({ type: 'ready', stats })
  }

  private _estimateRemainingMs(progress: Record<string, any>): number | null {
    const received = finiteNumber(progress.receivedDocuments)
    const at = typeof progress.at === 'string' ? Date.parse(progress.at) : Date.now()
    if (received == null || !Number.isFinite(at)) return null
    const key = `${progress.profileId ?? ''}\0${progress.scopeId ?? ''}`
    const baseline = this._downloadBaseline
    if (!baseline || baseline.key !== key || received < baseline.received) {
      this._downloadBaseline = { key, at, received }
      return null
    }
    const elapsed = at - baseline.at
    const gained = received - baseline.received
    if (elapsed < 2000 || gained <= 0) return null
    const docsLeft = documentsStillExpected(progress, this._state.stats, received)
    if (docsLeft == null) return null
    return Math.max(0, Math.round(docsLeft * elapsed / gained))
  }

  markFailed(error: any, context: LocalModelErrorContext = {
    code: 'LOCAL_MODEL_INITIALIZATION_FAILED',
    stage: 'initialization',
  }) {
    const normalized = normalizeError(error, context)
    this._dispatch({ type: 'failed', error: normalized })
  }

  markDegraded(error: any, context: LocalModelErrorContext = {
    code: 'LOCAL_MODEL_SYNCHRONIZATION_FAILED',
    stage: 'synchronization',
  }) {
    const normalized = normalizeError(error, context)
    this._dispatch({ type: 'degraded', error: normalized })
  }

  recordRoute(route: Exclude<LocalModelRoute, null>, reason?: string, sample?: LocalModelRouteSample) {
    const hitServer = route === 'remote' || route === 'mixed'
    if (sample && Number.isFinite(sample.ms) && sample.ms >= 0) {
      const ring = hitServer ? this._remoteDurations : this._localDurations
      ring.push(sample.ms)
      if (ring.length > LATENCY_SAMPLES) ring.shift()
    }
    this._dispatch({
      type: 'route-recorded',
      route,
      reason,
      sample,
      localLatency: latencyFrom(this._localDurations),
      remoteLatency: latencyFrom(this._remoteDurations),
    })
  }

  private _dispatch(event: LocalModelEvent) {
    this._state = transitionLocalModel(this._state, event)
    const state = this.state
    for (const listener of this._listeners) {
      try { listener(state) } catch { /* A UI subscriber cannot break routing. */ }
    }
  }
}
